import React from 'react';
import { remember } from '../trail';
import { forgetViewChoice } from '../store';
import { theReferenceOnScreen } from '../reporting';
import { BUILD_LABEL, forced } from '../env';

/**
 * WHAT IS ON SCREEN WHEN THE DRAWING OF IT FAILS.
 *
 * WHAT HAPPENED WITHOUT THIS. On 25 August 2026 Vinnie reported "uat does not
 * load" — a blank white page, on an address that loaded normally for me on the
 * same build. Nothing was on screen and nothing was written down. Half an hour
 * went into ruling out the delivery of the page, a stale cached copy, an asset
 * name that no longer existed and a service worker, none of which it was,
 * before the only way forward was to ask him to open the developer console
 * himself. The same evening, twice, the same white page meant two entirely
 * different faults. #254.
 *
 * WHY WHITE IS THE WORST POSSIBLE ANSWER. Every fault in this app is reported
 * by a person, and a blank page is the one report that carries no information
 * at all — not even enough for the person to tell whether the app is broken,
 * their connection is down, or they have been signed out. It costs an
 * investigation before the real question can be asked.
 *
 * IT MUST NOT SWALLOW THE FAULT. A screen that says "something went wrong" and
 * records nothing is the same blindness with better manners. So: the fault goes
 * to the console in full, it goes into the trail a feedback report carries, and
 * a short reference is put ON SCREEN so what somebody quotes can be matched to
 * what was recorded.
 *
 * WHAT IT DOES NOT CATCH, and this is not a gap being papered over. React
 * boundaries see errors thrown while RENDERING. They do not see a handler that
 * throws when a button is pressed, nor a promise nobody awaited. Those are
 * already caught by the window listeners in `watchForTheTrail`, which write to
 * the same trail — so between the two, a fault is recorded wherever it comes
 * from. The difference is that only a render fault takes the screen down, which
 * is why only this one needs something to put back in its place.
 */

/** Short, unambiguous when read out loud, and not a word. Reads back cleanly over a phone. */
const CODE = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const reference = () =>
  Array.from({ length: 6 }, () => CODE[Math.floor(Math.random() * CODE.length)])
    .join('')
    .replace(/^(.{3})/, '$1-');

/**
 * WHETHER RELOADING HAS ALREADY BEEN TRIED, AND FAILED.
 *
 * WHAT WENT WRONG WITHOUT THIS. The witness drove `?show=broken`, pressed the
 * button this screen leads with, and landed straight back on the same screen
 * with a new reference — because `reload()` keeps the address, and an address
 * is exactly what a repeatable fault repeats for. A bad issue id, a document
 * that will not open, a query string: for every one of those the prominent
 * action was a loop, and the working action was the quiet link beside it.
 *
 * SO THE OFFER CHANGES AFTER IT HAS BEEN TRIED. Reloading is still the right
 * first thing — most faults here are transient and it fixes them — but a second
 * failure at the same address is evidence, and the screen should act on it
 * rather than offer the same button again.
 *
 * PER TAB AND PER ADDRESS. `sessionStorage` so another tab's fault is not this
 * tab's history, and keyed by the address so moving on and breaking somewhere
 * else gets the honest first offer again.
 */
const TRIED = 'flightdeck-broke-tried';
const here = () => {
  try { return window.location.href; } catch { return ''; }
};
/* Wrapped because storage throws outright in Safari's private mode, and a
   screen that exists to survive a failure must not fail. */
const triedAlready = () => {
  try { return sessionStorage.getItem(TRIED) === here(); } catch { return false; }
};
const noteTried = () => {
  try { sessionStorage.setItem(TRIED, here()); } catch { /* nothing to do */ }
};
const forgetTried = () => {
  try { sessionStorage.removeItem(TRIED); } catch { /* nothing to do */ }
};

/**
 * WHETHER STARTING AGAIN HAS ALREADY BEEN TRIED, AND FAILED — #322.
 *
 * WHAT WENT WRONG WITHOUT THIS, and it is the whole reason the card exists.
 * The two offers above are a ladder with a top rung missing. "Start again"
 * sends somebody to `/`, and if the app is broken AT `/` — which is what a
 * remembered screen, a bad board or a fault in the shell all look like — then
 * the screen it lands on offers "Reload the page", then "Start again from the
 * beginning", which is where they already are. That is a circle, not an escape.
 *
 * Vinnie, 30 August 2026: "there has to be a way for the customer to go back to
 * a working environment, if the screen shows up and they are stopped from
 * working that is not good." He had spent the previous day inside that circle
 * and got out with a private window.
 *
 * Cleared the moment a page draws, so this is only ever set for somebody who
 * started again and arrived at a broken screen anyway.
 */
const RESTARTED = 'flightdeck-broke-restarted';
const restartedAlready = () => {
  try { return sessionStorage.getItem(RESTARTED) === '1'; } catch { return false; }
};
const noteRestarted = () => {
  try { sessionStorage.setItem(RESTARTED, '1'); } catch { /* nothing to do */ }
};
const forgetRestarted = () => {
  try { sessionStorage.removeItem(RESTARTED); } catch { /* nothing to do */ }
};

/**
 * START AGAIN, PROPERLY — and the reason this is a function rather than two
 * lines in an onClick is that forgetting the remembered screen is the part that
 * actually works, and it must not be possible to add a third caller that omits
 * it.
 */
const startAgain = () => {
  forgetTried();
  forgetViewChoice();
  noteRestarted();
  window.location.assign('/');
};

/**
 * THE LAST RUNG: what a private window does, without asking somebody to know
 * what a private window is.
 *
 * Everything this browser remembers about being signed in, and where they were,
 * is dropped. That is the whole of it — NOTHING ON THEIR BOARD IS TOUCHED and
 * nothing is sent anywhere. It lands on the sign-in screen, which is the one
 * screen in the app that draws with no data behind it and therefore the one
 * that cannot be broken by any of theirs.
 *
 * IT DOES NOT WAIT FOR THE SERVER. Calling `signOut` and awaiting it would put
 * a network round trip in front of somebody who is already stuck, and if it
 * hung, the escape hatch would hang. So the session row is left to expire and
 * only this browser is cleared — which is what "start clean" has to mean here,
 * and is why the wording does not promise more.
 */
const signOutOfThisBrowser = () => {
  try {
    forgetViewChoice();
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) localStorage.removeItem(key);
    }
  } catch {
    /* private browsing, or storage refused — the navigation below still helps */
  }
  forgetTried();
  forgetRestarted();
  window.location.assign('/');
};

/**
 * THE SCREEN ITSELF, SEPARATE FROM THE CATCHING — and separate for a reason
 * rather than for tidiness. `renderToStaticMarkup` does not honour error
 * boundaries: it throws straight past them. So a screen test can never reach
 * this by breaking something, and if the wording lived inside the boundary it
 * would be the one screen in the app that nothing draws and nobody reviews.
 * Drawn on its own it is checked like every other screen; that the boundary
 * REACHES it is proved in a real browser instead, with `?show=broken`.
 */
export function BrokenScreen({
  said,
  reference: ref,
  again = false,
  stuck = false,
}: {
  said: string;
  reference: string;
  /** True when a reload was already tried at this address and did not help. */
  again?: boolean;
  /** True when starting again was tried and the page broke anyway — #322. */
  stuck?: boolean;
}) {
  return (
    <div className="broke-wrap" role="alert">
      <div className="broke">
        <h1>This screen did not load</h1>
        <p>
          Something went wrong while drawing the page. Nothing you did caused it and nothing
          you had saved is lost.
        </p>
        {/* A reload is the honest first thing and fixes most of these, so it leads
            — until it has been tried at this address and did not help, at which
            point continuing to lead with it is a loop rather than an offer.
            Starting again is second to begin with because an expired session
            looks identical from here — see #261. */}
        {stuck ? (
          <>
            {/* THE TOP OF THE LADDER. Everything gentler has been tried and the
                page is still broken, so the offer stops being "try that again"
                and becomes the thing that always works. Said plainly, because
                somebody reading this has been stuck for a while by now. */}
            <p className="broke-again">
              Starting again did not fix it either. Signing out of this browser clears
              what it remembers and will get you back to a working screen.{' '}
              <strong>Nothing on your board is changed or lost.</strong>
            </p>
            <div className="broke-do">
              <button className="btn btn-primary" onClick={signOutOfThisBrowser}>
                Sign out and start clean
              </button>
              <button
                className="broke-away broke-plain"
                onClick={() => {
                  noteTried();
                  window.location.reload();
                }}
              >
                Try reloading once more
              </button>
            </div>
          </>
        ) : again ? (
          <>
            <p className="broke-again">
              Reloading did not fix it, so something about this page in particular is
              wrong. Starting again is the better bet.
            </p>
            <div className="broke-do">
              <button className="btn btn-primary" onClick={startAgain}>
                Start again from the beginning
              </button>
              <button
                className="broke-away broke-plain"
                onClick={() => {
                  noteTried();
                  window.location.reload();
                }}
              >
                Try reloading once more
              </button>
            </div>
          </>
        ) : (
          <div className="broke-do">
            <button
              className="btn btn-primary"
              onClick={() => {
                noteTried();
                window.location.reload();
              }}
            >
              Reload the page
            </button>
            {/* A BUTTON, NOT A LINK, and that is the fix rather than a style
                choice. As an `<a href="/">` this navigated before anything could
                forget the remembered screen — so it went back to the screen that
                was broken, which is precisely what it promised not to do. */}
            <button className="broke-away broke-plain" onClick={startAgain}>
              Start again from the beginning
            </button>
          </div>
        )}
        {/*
          * WHAT IT SAYS NOW THAT IT SENDS ITSELF — #265.
          *
          * It used to ask the reader to tell somebody, and that was the only
          * way a fault ever reached us. It no longer is. Saying so is not
          * politeness: this screen is read by an insurance company, something
          * has just left their browser, and a product that collects and does
          * not mention it has decided for them. Vinnie chose this wording on
          * 30 August 2026 over leaving it as it was.
          *
          * THE REFERENCE STILL MATTERS, and more than before, because it now
          * resolves to something. It is how "our board died this morning"
          * becomes one row.
          */}
        <p className="broke-ref">
          We have been told about this. If you need to follow it up, quote{' '}
          <strong>{ref}</strong>.
        </p>
        {/* The message itself, last and quiet. It is written for developers and
            will mean nothing to most people — but it is the difference between
            a report that can be acted on and "it did not work", and hiding it
            helps nobody. */}
        <p className="broke-said">
          {said}
          <span className="broke-build"> · {BUILD_LABEL}</span>
        </p>
      </div>
    </div>
  );
}

/** Throws while rendering, so `?show=broken` proves the catch rather than the screen. */
function Deliberately(): React.ReactElement {
  throw new Error('Summoned with ?show=broken — nothing is actually wrong.');
}

interface Broken {
  /** Empty means nothing has gone wrong; this is not a boolean, it is the message. */
  said: string;
  ref: string;
  /** A reload was already tried at this address and the fault came back. */
  again: boolean;
  /** Starting again was tried and it broke anyway — the last rung. #322 */
  stuck: boolean;
}

export default class WhenItBreaks extends React.Component<
  { children: React.ReactNode },
  Broken
> {
  state: Broken = { said: '', ref: '', again: false, stuck: false };

  static getDerivedStateFromError(e: Error): Broken {
    return {
      said: e.message || 'Something went wrong drawing the page.',
      ref: reference(),
      again: triedAlready(),
      stuck: restartedAlready(),
    };
  }

  componentDidCatch(e: Error, info: React.ErrorInfo) {
    const { ref } = this.state;
    /*
     * THE CONSOLE GETS EVERYTHING, and it is written as one group so that the
     * reference somebody reads off the screen is beside the stack rather than
     * scrolled away from it. React logs the error itself as well; this adds the
     * reference, which is the only part that ties a person's report to a line.
     */
    console.error(`FlightDeck broke — reference ${ref}`, e, info.componentStack);
    /*
     * And into the trail. THE MESSAGE ONLY GOES INTO THE RING, which is what a
     * feedback report carries when a person writes one: that report crosses
     * from a customer's workspace into ours, and a component stack in it is
     * more than was asked for.
     *
     * THE STACKS GO PAST IT to the reporter, and are kept nowhere else — see
     * `remember`. Both of them, because they answer different questions and the
     * day this card was built for needed the second: on 30 August 2026 the
     * JavaScript stack was thirty frames of `Bm` and `B0` and said nothing,
     * and the COMPONENT stack named `SignOff` in one line. That table is ours,
     * is never shown to a customer, and holds no board data.
     */
    /*
     * THE REFERENCE IS HANDED OVER BEFORE THE FAULT IS RECORDED, and the order
     * is the whole of whether #265 works. `remember` is what sends it, so a
     * reference set afterwards would arrive on the NEXT fault and the row for
     * this one would carry none — leaving exactly the gap this closes: a person
     * quoting a code that matches nothing.
     */
    theReferenceOnScreen(ref);
    remember('broke', `render: ${e.message} [${ref}]`, `${e.stack ?? ''}\n${info.componentStack ?? ''}`.trim());
  }

  /*
   * A PAGE THAT DREW IS A PAGE THAT RECOVERED. Clearing the marker here rather
   * than on the way out means a reload that WORKED leaves no trace, so the next
   * fault — days later, somewhere else entirely — is met with the plain first
   * offer instead of "reloading did not fix it", which would be a lie.
   */
  componentDidMount() {
    if (this.state.said) return;
    forgetTried();
    /* And the same for the top rung: somebody who started again and landed on a
       page that DREW is not stuck, so the next fault must not open on "starting
       again did not fix it either", which would be a lie and a frightening one. */
    forgetRestarted();
  }

  render() {
    const { said, ref, again, stuck } = this.state;
    /*
     * A WAY TO SEE IT WORKING, on a test copy only. `forced` refuses on live —
     * decided by the hostname AND the build, so no query string can talk it
     * into it — and `test-forced-screens` holds that shut. Placed here so the
     * throw happens INSIDE the boundary, which makes it a proof of the catch
     * and not merely a preview of the screen.
     */
    if (!said && forced('broken')) return <Deliberately />;
    if (!said) return this.props.children;
    return <BrokenScreen said={said} reference={ref} again={again} stuck={stuck} />;
  }
}
